import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { FormularioConcursoAdmin } from '../../../../core/models/admin-formularios.models';
import { forkJoin } from 'rxjs';
import { CVData } from '../../../../core/models/cv.models';
import { RolPlataforma } from '../../../../core/models/novedades.models';
import { DatosPersonales } from '../../../../core/models/perfil.models';
import {
  FormularioPostulacionData,
  InstitucionFormulario,
  Postulacion,
  PresupuestoInstitucionFormulario,
  PresupuestoItemFormulario,
  PresupuestoSubitemFormulario,
} from '../../../../core/models/postulaciones.models';
import { AdminFormulariosService } from '../../../../core/services/admin-formularios.service';
import { CvService } from '../../../../core/services/cv.service';
import { PerfilService } from '../../../../core/services/perfil.service';
import { PostulacionesLogService } from '../../../../core/services/postulaciones-log.service';
import { PostulacionesService } from '../../../../core/services/postulaciones.service';
import { RoleContextService } from '../../../../core/services/role-context.service';
import { etiquetaEstadoPostulacion } from '../../../../core/utils/postulaciones-filter.util';
import { AccessibilityToolbar } from '../../../../shared/components/accessibility-toolbar/accessibility-toolbar';
import { BreadcrumbItem, Breadcrumbs } from '../../../../shared/components/breadcrumbs/breadcrumbs';
import { UserSessionSummary } from '../../../../shared/components/user-session-summary/user-session-summary';
import { SidebarMenu } from '../../../../shared/sidebar-menu/sidebar-menu';
import { PostulacionPasoDocumentos } from './pasos/postulacion-paso-documentos';
import { PostulacionPasoEquipoTrabajo } from './pasos/postulacion-paso-equipo-trabajo';
import { PostulacionPasoInformacionGeneral } from './pasos/postulacion-paso-informacion-general';
import { PostulacionPasoInstituciones } from './pasos/postulacion-paso-instituciones';
import { PostulacionPasoPresupuesto } from './pasos/postulacion-paso-presupuesto';
import { PostulacionPasoRevisionEnvio } from './pasos/postulacion-paso-revision-envio';
import { AvanceSeccionFormulario, FormularioEditable, PasoFormulario, PasoItem, RevisionSeccionFormulario } from './postulacion-formulario.models';
type ModoFormulario = 'edicion' | 'solo_lectura';

interface PermisosFormulario {
  visible: boolean;
  modo: ModoFormulario;
  puedeGuardar: boolean;
  puedeEnviar: boolean;
  motivo?: string;
}

interface TextosFormularioConcurso {
  nombreFormulario: string;
  descripcion: string;
  instrucciones: string;
  mensajeSistema: string;
}

@Component({
  selector: 'app-postulacion-formulario',
  imports: [
    CommonModule,
    FormsModule,
    SidebarMenu,
    AccessibilityToolbar,
    Breadcrumbs,
    UserSessionSummary,
    PostulacionPasoInformacionGeneral,
    PostulacionPasoInstituciones,
    PostulacionPasoEquipoTrabajo,
    PostulacionPasoPresupuesto,
    PostulacionPasoDocumentos,
    PostulacionPasoRevisionEnvio,
  ],
  templateUrl: './postulacion-formulario.html',
  styleUrl: './postulacion-formulario.css',
})
export class PostulacionFormulario {
  readonly pasos: PasoItem[] = [
    { key: 'informacion_general', label: 'Informacion general' },
    { key: 'instituciones', label: 'Instituciones' },
    { key: 'equipo_trabajo', label: 'Equipo de trabajo' },
    { key: 'presupuesto', label: 'Presupuesto' },
    { key: 'documentos', label: 'Documentos' },
    { key: 'revision_envio', label: 'Revision y envio' },
  ];

  rol: RolPlataforma = 'postulante';
  idPostulacion = '';
  postulacion: Postulacion | null = null;
  permisos: PermisosFormulario = {
    visible: false,
    modo: 'solo_lectura',
    puedeGuardar: false,
    puedeEnviar: false,
  };
  pasoActivo: PasoFormulario = 'informacion_general';
  formulario: FormularioEditable = {
    nombreProyecto: '',
    departamentoFacultad: '',
    nombreInstitucion: '',
    nombreProgramaEstudios: '',
    regionProgramaEstudios: '',
    especialidadWos: '',
    subAreaOcde: '',
    sitioWebUniversidad: '',
    sitioWebPrograma: '',
    fechaInicioEstudios: '',
    estadoProgramaEstudios: '',
    correoContacto: '',
    telefonoContacto: '',
    resumenProyecto: '',
    objetivoGeneral: '',
    lineaInvestigacion: '',
    institucionPatrocinante: '',
    unidadInstitucional: '',
    recomendadorAsignado: '',
    colaboradorAsignado: '',
    patrocinioSolicitado: '',
    patrocinadorAsignado: '',
    instituciones: [],
    registroParticipantes: [],
    duracionPresupuestoAnios: 3,
    presupuestoPorInstitucion: [],
    presupuestoItems: [],
    recursosHumanos: 0,
    equipamiento: 0,
    infraestructura: 0,
    operacionFuncionamiento: 0,
    viajesViaticos: 0,
    publicacionesDifusion: 0,
    serviciosExternos: 0,
    softwareLicencias: 0,
    capacitacionFormacion: 0,
    materialesInsumos: 0,
    totalSolicitado: 0,
    documentosAdjuntos: [],
  };
  mensaje = 'Cargando formulario de postulacion...';
  mensajeAutocompletado = '';
  mensajePresupuestoGuardado = '';
  textosConcurso: TextosFormularioConcurso | null = null;
  datosPersonales: DatosPersonales | null = null;
  guardando = false;
  hayCambiosPendientes = false;
  pendientesEnvioVisible = false;
  confirmacionEnvioVisible = false;
  enviandoPostulacion = false;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly roleContext: RoleContextService,
    private readonly postulacionesService: PostulacionesService,
    private readonly adminFormulariosService: AdminFormulariosService,
    private readonly perfilService: PerfilService,
    private readonly cvService: CvService,
    private readonly postulacionesLogService: PostulacionesLogService
  ) {
    this.route.queryParamMap.subscribe((params) => {
      this.rol = this.roleContext.resolveRole(params, this.rol);
      this.cargarFormulario();
    });

    this.route.paramMap.subscribe((params) => {
      this.idPostulacion = params.get('id') ?? '';
      this.cargarFormulario();
    });
  }

  get breadcrumbs(): BreadcrumbItem[] {
    return [
      { label: 'Inicio', link: '/inicio' },
      { label: 'Mi perfil', link: '/mi-perfil' },
      { label: this.idPostulacion || 'Formulario' },
    ];
  }

  get esSoloLectura(): boolean {
    return this.permisos.modo === 'solo_lectura';
  }

  get tituloFormularioVisible(): string {
    return this.textosConcurso?.nombreFormulario || 'Formulario de postulacion';
  }

  get rolActivoLabel(): string {
    return this.roleContext.getRoleLabel(this.rol);
  }

  get mostrarTextosConcurso(): boolean {
    return !!(this.textosConcurso?.descripcion || this.textosConcurso?.instrucciones || this.textosConcurso?.mensajeSistema);
  }

  get etiquetaEstadoPostulacionActual(): string {
    return this.postulacion ? etiquetaEstadoPostulacion(this.postulacion.estadoPostulacion) : '';
  }

  get esConcursoBeca(): boolean {
    return this.postulacion ? this.postulacion.concurso.toLowerCase().includes('beca') || this.postulacion.concurso.toLowerCase().includes('capital humano') : false;
  }

  get mostrarSeccionProyecto(): boolean {
    return !this.esConcursoBeca;
  }

  get indicePasoActivo(): number {
    return this.pasos.findIndex((paso) => paso.key === this.pasoActivo);
  }

  get tituloPasoActivo(): string {
    return `${this.indicePasoActivo + 1}. ${this.pasos[this.indicePasoActivo]?.label ?? 'Formulario'}`;
  }

  get puedeRetroceder(): boolean {
    return this.indicePasoActivo > 0;
  }

  get puedeAvanzar(): boolean {
    return this.indicePasoActivo > -1 && this.indicePasoActivo < this.pasos.length - 1;
  }

  get seccionesRevision(): RevisionSeccionFormulario[] {
    return [
      {
        key: 'informacion_general',
        label: 'Informacion general',
        detalle: 'Base del formulario y antecedentes del programa.',
        completa: this.validarInformacionGeneral(),
      },
      {
        key: 'instituciones',
        label: 'Instituciones',
        detalle: 'Contexto institucional y unidad responsable.',
        completa: this.validarInstituciones(),
      },
      {
        key: 'equipo_trabajo',
        label: 'Equipo de trabajo',
        detalle: 'Integrantes, recomendador y patrocinio.',
        completa: this.validarEquipoTrabajo(),
      },
      {
        key: 'presupuesto',
        label: 'Presupuesto',
        detalle: 'Montos principales de la postulacion.',
        completa: this.validarPresupuesto(),
      },
      {
        key: 'documentos',
        label: 'Documentos',
        detalle: 'Adjuntos requeridos y anexos del concurso.',
        completa: this.validarDocumentos(),
      },
    ];
  }

  get seccionesPendientes(): RevisionSeccionFormulario[] {
    return this.seccionesRevision.filter((seccion) => !seccion.completa);
  }

  get avanceGeneral(): number {
    if (!this.postulacion) return 0;
    return this.postulacionesService.calcularAvancePostulacion(this.construirPostulacionActual());
  }

  get avanceSecciones(): AvanceSeccionFormulario[] {
    const seccionesBase = this.seccionesRevision;
    const todasCompletas = seccionesBase.every((seccion) => seccion.completa);

    return [
      this.crearAvanceSeccion('informacion_general', 'Informacion general', 'Datos del programa y base del proyecto.', [
        this.formulario.nombreInstitucion,
        this.formulario.nombreProgramaEstudios,
        this.formulario.regionProgramaEstudios,
        this.formulario.especialidadWos,
        this.formulario.subAreaOcde,
        this.formulario.fechaInicioEstudios,
        this.formulario.estadoProgramaEstudios,
        ...(this.mostrarSeccionProyecto
          ? [this.formulario.nombreProyecto, this.formulario.resumenProyecto, this.formulario.objetivoGeneral]
          : []),
      ]),
      this.crearAvanceSeccion('instituciones', 'Instituciones', 'Contexto institucional y unidad responsable.', [
        this.formulario.institucionPatrocinante,
        this.formulario.unidadInstitucional,
        this.formulario.departamentoFacultad,
      ]),
      this.crearAvanceSeccion('equipo_trabajo', 'Equipo de trabajo', 'Invitaciones y patrocinio del proceso.', [
        this.formulario.recomendadorAsignado,
        this.formulario.colaboradorAsignado,
      ]),
      this.crearAvanceSeccion('presupuesto', 'Presupuesto', 'Detalle estructurado de recursos del proyecto.', [
        this.formulario.recursosHumanos,
        this.formulario.equipamiento,
        this.formulario.infraestructura,
        this.formulario.operacionFuncionamiento,
        this.formulario.viajesViaticos,
        this.formulario.publicacionesDifusion,
        this.formulario.serviciosExternos,
        this.formulario.softwareLicencias,
        this.formulario.capacitacionFormacion,
        this.formulario.materialesInsumos,
        this.formulario.totalSolicitado,
      ]),
      this.crearAvanceSeccion('documentos', 'Documentos', 'Adjuntos obligatorios y anexos del concurso.', [
        this.tieneDocumentoCargado('carta_compromiso'),
        this.tieneDocumentoCargado('cv_postulante'),
        this.tieneDocumentoCargado('presupuesto_financiero'),
      ]),
      this.crearAvanceSeccion('revision_envio', 'Revision y envio', 'Chequeo final y envio oficial de la postulacion.', [
        todasCompletas,
        this.postulacion?.estadoPostulacion === 'enviada',
      ]),
    ];
  }

  get seccionesCompletas(): number {
    return this.avanceSecciones.filter((seccion) => seccion.completa).length;
  }

  get envioHabilitado(): boolean {
    return this.permisos.puedeEnviar && this.seccionesPendientes.length === 0 && !this.enviandoPostulacion;
  }

  seleccionarPaso(paso: PasoFormulario): void {
    if (paso === this.pasoActivo) return;

    if (!this.hayCambiosPendientes || !this.permisos.puedeGuardar || !this.postulacion) {
      this.activarPaso(paso);
      return;
    }

    this.guardarFormulario(true, () => {
      this.activarPaso(paso);
    });
  }

  irAPasoAnterior(): void {
    if (!this.puedeRetroceder) return;
    const pasoAnterior = this.pasos[this.indicePasoActivo - 1];
    if (pasoAnterior) {
      this.seleccionarPaso(pasoAnterior.key);
    }
  }

  guardarYContinuar(): void {
    if (!this.puedeAvanzar) return;

    const pasoSiguiente = this.pasos[this.indicePasoActivo + 1];
    if (!pasoSiguiente) return;

    if (this.hayCambiosPendientes && this.permisos.puedeGuardar) {
      this.guardarFormulario(true, () => {
        this.activarPaso(pasoSiguiente.key);
      });
      return;
    }

    this.activarPaso(pasoSiguiente.key);
  }

  guardarBorrador(): void {
    this.guardarFormulario(false);
  }

  guardarPresupuestoDesdeModal(): void {
    this.mensajePresupuestoGuardado = '';
    this.guardarFormulario(false, () => {
      this.mensajePresupuestoGuardado = 'Presupuesto guardado correctamente.';
    });
  }

  intentarEnviarPostulacion(): void {
    if (!this.permisos.puedeEnviar || this.enviandoPostulacion) return;

    if (this.seccionesPendientes.length > 0) {
      this.pendientesEnvioVisible = true;
      this.confirmacionEnvioVisible = false;
      return;
    }

    this.confirmacionEnvioVisible = true;
    this.pendientesEnvioVisible = false;
  }

  cerrarPendientesEnvio(): void {
    this.pendientesEnvioVisible = false;
  }

  cerrarConfirmacionEnvio(): void {
    this.confirmacionEnvioVisible = false;
  }

  irASeccionPendiente(paso: PasoFormulario): void {
    this.pendientesEnvioVisible = false;
    this.seleccionarPaso(paso);
  }

  confirmarEnvioPostulacion(): void {
    if (!this.postulacion || !this.permisos.puedeEnviar || this.enviandoPostulacion) return;

    this.confirmacionEnvioVisible = false;
    this.enviandoPostulacion = true;
    this.postulacionesService
      .enviarPostulacion(this.postulacion.idPostulacion, {
        nombreProyecto: this.formulario.nombreProyecto.trim(),
        departamentoFacultad: this.formulario.departamentoFacultad.trim(),
        formularioData: this.extraerFormularioData(),
      })
      .subscribe((item) => {
        this.enviandoPostulacion = false;

        if (!item) {
          this.mensaje = 'No fue posible enviar la postulacion en esta etapa.';
          return;
        }

        this.postulacion = item;
        this.permisos = this.resolverPermisos(item);
        this.sincronizarFormulario(item);
        this.mensaje = `Postulacion ${item.idPostulacion} enviada correctamente.`;

        this.postulacionesLogService.registrar({
          timestamp: new Date().toISOString(),
          rol: this.rol,
          idPostulacion: item.idPostulacion,
          folio: item.folio,
          accion: 'enviar_postulacion',
          detalle: 'Envia la postulacion y la deja registrada para evaluacion.',
        });
      });
  }

  registrarCambio(): void {
    if (!this.esSoloLectura) {
      if (this.pasoActivo === 'presupuesto') {
        this.mensajePresupuestoGuardado = '';
      }
      this.hayCambiosPendientes = true;
      if (this.postulacion) {
        this.postulacion = {
          ...this.postulacion,
          avancePorcentaje: this.avanceGeneral,
        };
      }
    }
  }

  private activarPaso(paso: PasoFormulario): void {
    if (paso === 'presupuesto') {
      this.prepararPresupuestoPorInstitucion();
    }

    this.pasoActivo = paso;
  }

  private cargarFormulario(): void {
    if (!this.idPostulacion) return;

    const perfilMock = this.route.snapshot.queryParamMap.get('perfil');

    forkJoin({
      postulacion: this.postulacionesService.getPostulacionById(this.idPostulacion),
      perfil: this.perfilService.getDatosPersonales(perfilMock),
      cv: this.cvService.getCv(),
    }).subscribe(({ postulacion, perfil, cv }) => {
      this.postulacion = postulacion;
      this.datosPersonales = perfil ?? null;
      this.permisos = this.resolverPermisos(postulacion);

      if (!postulacion || !this.permisos.visible) {
        this.mensaje = this.permisos.motivo ?? 'No fue posible acceder a la postulacion solicitada.';
        this.textosConcurso = null;
        return;
      }

      this.sincronizarFormulario(postulacion, perfil, cv);
      this.cargarTextosConcurso(postulacion);
      this.mensaje =
        this.permisos.modo === 'edicion'
          ? 'Formulario base en modo edicion para continuar la postulacion.'
          : 'Formulario disponible en modo solo lectura segun el rol y estado actual.';
    });
  }

  private sincronizarFormulario(item: Postulacion, perfil?: DatosPersonales, cv?: CVData): void {
    const formularioData = this.aplicarAutocompletado(item.formularioData ?? this.crearFormularioVacio(), perfil, cv);
    const departamentoFacultad = this.resolverDepartamentoFacultad(item.departamentoFacultad, perfil);

    this.formulario = {
      nombreProyecto: item.nombreProyecto,
      departamentoFacultad,
      nombreInstitucion: formularioData.nombreInstitucion,
      nombreProgramaEstudios: formularioData.nombreProgramaEstudios,
      regionProgramaEstudios: formularioData.regionProgramaEstudios,
      especialidadWos: formularioData.especialidadWos,
      subAreaOcde: formularioData.subAreaOcde,
      sitioWebUniversidad: formularioData.sitioWebUniversidad,
      sitioWebPrograma: formularioData.sitioWebPrograma,
      fechaInicioEstudios: formularioData.fechaInicioEstudios,
      estadoProgramaEstudios: formularioData.estadoProgramaEstudios,
      correoContacto: formularioData.correoContacto,
      telefonoContacto: formularioData.telefonoContacto,
      resumenProyecto: formularioData.resumenProyecto,
      objetivoGeneral: formularioData.objetivoGeneral,
      lineaInvestigacion: formularioData.lineaInvestigacion,
      institucionPatrocinante: formularioData.institucionPatrocinante,
      unidadInstitucional: formularioData.unidadInstitucional,
      recomendadorAsignado: formularioData.recomendadorAsignado,
      colaboradorAsignado: formularioData.colaboradorAsignado,
      patrocinioSolicitado: formularioData.patrocinioSolicitado,
      patrocinadorAsignado: formularioData.patrocinadorAsignado,
      instituciones: this.clonarInstituciones(formularioData.instituciones, {
        institucionPatrocinante: formularioData.institucionPatrocinante,
        unidadInstitucional: formularioData.unidadInstitucional,
        departamentoFacultad,
      }),
      registroParticipantes: [...formularioData.registroParticipantes],
      duracionPresupuestoAnios: this.resolverDuracionPresupuestoAnios(formularioData),
      presupuestoPorInstitucion: this.clonarPresupuestoPorInstitucion(formularioData.presupuestoPorInstitucion),
      presupuestoItems: this.clonarPresupuestoItems(formularioData.presupuestoItems),
      recursosHumanos: formularioData.recursosHumanos,
      equipamiento: formularioData.equipamiento,
      infraestructura: formularioData.infraestructura,
      operacionFuncionamiento: formularioData.operacionFuncionamiento,
      viajesViaticos: formularioData.viajesViaticos,
      publicacionesDifusion: formularioData.publicacionesDifusion,
      serviciosExternos: formularioData.serviciosExternos,
      softwareLicencias: formularioData.softwareLicencias,
      capacitacionFormacion: formularioData.capacitacionFormacion,
      materialesInsumos: formularioData.materialesInsumos,
      totalSolicitado: formularioData.totalSolicitado,
      documentosAdjuntos: [...formularioData.documentosAdjuntos],
    };
    if (this.pasoActivo === 'presupuesto') {
      this.prepararPresupuestoPorInstitucion();
    }
    this.hayCambiosPendientes = false;
  }

  private cargarTextosConcurso(postulacion: Postulacion): void {
    this.adminFormulariosService.getFormularioPorConcurso(postulacion.concurso).subscribe((formulario) => {
      this.textosConcurso = this.mapearTextosFormulario(formulario);
    });
  }

  private guardarFormulario(automatico: boolean, onSuccess?: () => void): void {
    if (!this.postulacion || !this.permisos.puedeGuardar || this.guardando) return;

    this.guardando = true;
    this.postulacionesService
      .guardarDetallePostulacion(this.postulacion.idPostulacion, {
        nombreProyecto: this.formulario.nombreProyecto.trim(),
        departamentoFacultad: this.formulario.departamentoFacultad.trim(),
        formularioData: this.extraerFormularioData(),
      })
      .subscribe((item) => {
        this.guardando = false;

        if (!item) {
          this.mensaje = automatico
            ? 'No fue posible guardar automaticamente al salir de la seccion.'
            : 'No fue posible guardar los cambios del borrador.';
          return;
        }

        this.postulacion = item;
        this.sincronizarFormulario(item);
        this.mensaje = automatico
          ? 'Cambios guardados automaticamente al cambiar de seccion.'
          : 'Cambios guardados en el borrador del formulario.';

        this.postulacionesLogService.registrar({
          timestamp: new Date().toISOString(),
          rol: this.rol,
          idPostulacion: item.idPostulacion,
          folio: item.folio,
          accion: 'editar_formulario',
          detalle: automatico
            ? 'Guarda cambios del formulario al salir de la seccion.'
            : 'Guarda cambios del formulario desde el borrador.',
        });
        onSuccess?.();
      });
  }

  private extraerFormularioData(): FormularioPostulacionData {
    return {
      nombreInstitucion: this.formulario.nombreInstitucion.trim(),
      nombreProgramaEstudios: this.formulario.nombreProgramaEstudios.trim(),
      regionProgramaEstudios: this.formulario.regionProgramaEstudios.trim(),
      especialidadWos: this.formulario.especialidadWos.trim(),
      subAreaOcde: this.formulario.subAreaOcde.trim(),
      sitioWebUniversidad: this.formulario.sitioWebUniversidad.trim(),
      sitioWebPrograma: this.formulario.sitioWebPrograma.trim(),
      fechaInicioEstudios: this.formulario.fechaInicioEstudios,
      estadoProgramaEstudios: this.formulario.estadoProgramaEstudios,
      correoContacto: this.formulario.correoContacto.trim(),
      telefonoContacto: this.formulario.telefonoContacto.trim(),
      resumenProyecto: this.formulario.resumenProyecto.trim(),
      objetivoGeneral: this.formulario.objetivoGeneral.trim(),
      lineaInvestigacion: this.formulario.lineaInvestigacion.trim(),
      institucionPatrocinante: this.formulario.institucionPatrocinante.trim(),
      unidadInstitucional: this.formulario.unidadInstitucional.trim(),
      recomendadorAsignado: this.formulario.recomendadorAsignado.trim(),
      colaboradorAsignado: this.formulario.colaboradorAsignado.trim(),
      patrocinioSolicitado: this.formulario.patrocinioSolicitado,
      patrocinadorAsignado: this.formulario.patrocinadorAsignado.trim(),
      instituciones: this.clonarInstituciones(this.formulario.instituciones, {
        institucionPatrocinante: this.formulario.institucionPatrocinante,
        unidadInstitucional: this.formulario.unidadInstitucional,
        departamentoFacultad: this.formulario.departamentoFacultad,
      }),
      registroParticipantes: [...this.formulario.registroParticipantes],
      duracionPresupuestoAnios: this.resolverDuracionPresupuestoAnios(this.formulario),
      presupuestoPorInstitucion: this.clonarPresupuestoPorInstitucion(this.formulario.presupuestoPorInstitucion),
      presupuestoItems: this.clonarPresupuestoItems(this.formulario.presupuestoItems),
      recursosHumanos: Number(this.formulario.recursosHumanos) || 0,
      equipamiento: Number(this.formulario.equipamiento) || 0,
      infraestructura: Number(this.formulario.infraestructura) || 0,
      operacionFuncionamiento: Number(this.formulario.operacionFuncionamiento) || 0,
      viajesViaticos: Number(this.formulario.viajesViaticos) || 0,
      publicacionesDifusion: Number(this.formulario.publicacionesDifusion) || 0,
      serviciosExternos: Number(this.formulario.serviciosExternos) || 0,
      softwareLicencias: Number(this.formulario.softwareLicencias) || 0,
      capacitacionFormacion: Number(this.formulario.capacitacionFormacion) || 0,
      materialesInsumos: Number(this.formulario.materialesInsumos) || 0,
      totalSolicitado: Number(this.formulario.totalSolicitado) || 0,
      documentosAdjuntos: [...this.formulario.documentosAdjuntos],
    };
  }

  private crearFormularioVacio(): FormularioPostulacionData {
    return {
      nombreInstitucion: '',
      nombreProgramaEstudios: '',
      regionProgramaEstudios: '',
      especialidadWos: '',
      subAreaOcde: '',
      sitioWebUniversidad: '',
      sitioWebPrograma: '',
      fechaInicioEstudios: '',
      estadoProgramaEstudios: '',
      correoContacto: '',
      telefonoContacto: '',
      resumenProyecto: '',
      objetivoGeneral: '',
      lineaInvestigacion: '',
      institucionPatrocinante: '',
      unidadInstitucional: '',
      recomendadorAsignado: '',
      colaboradorAsignado: '',
      patrocinioSolicitado: '',
      patrocinadorAsignado: '',
      instituciones: [],
      registroParticipantes: [],
      duracionPresupuestoAnios: 3,
      presupuestoPorInstitucion: [],
      presupuestoItems: [],
      recursosHumanos: 0,
      equipamiento: 0,
      infraestructura: 0,
      operacionFuncionamiento: 0,
      viajesViaticos: 0,
      publicacionesDifusion: 0,
      serviciosExternos: 0,
      softwareLicencias: 0,
      capacitacionFormacion: 0,
      materialesInsumos: 0,
      totalSolicitado: 0,
      documentosAdjuntos: [],
    };
  }

  private aplicarAutocompletado(
    formularioData: FormularioPostulacionData,
    perfil?: DatosPersonales,
    cv?: CVData
  ): FormularioPostulacionData {
    const resultado: FormularioPostulacionData = {
      ...formularioData,
      instituciones: this.clonarInstituciones(formularioData.instituciones),
      documentosAdjuntos: [...formularioData.documentosAdjuntos],
    };
    const fuentesAplicadas: string[] = [];

    if (perfil) {
      if (!resultado.nombreInstitucion.trim() && perfil.institucionActual.trim()) {
        resultado.nombreInstitucion = perfil.institucionActual.trim();
        fuentesAplicadas.push('institucion del perfil');
      }
      if (!resultado.regionProgramaEstudios.trim() && perfil.region.trim()) {
        resultado.regionProgramaEstudios = perfil.region.trim();
        fuentesAplicadas.push('region del perfil');
      }
      if (!resultado.correoContacto.trim() && perfil.correoPrincipal.trim()) {
        resultado.correoContacto = perfil.correoPrincipal.trim();
        fuentesAplicadas.push('correo principal');
      }
      if (!resultado.telefonoContacto.trim() && perfil.telefono.trim()) {
        resultado.telefonoContacto = perfil.telefono.trim();
        fuentesAplicadas.push('telefono');
      }
      if (!resultado.institucionPatrocinante.trim() && perfil.institucionActual.trim()) {
        resultado.institucionPatrocinante = perfil.institucionActual.trim();
        fuentesAplicadas.push('institucion patrocinante');
      }
      if (!resultado.unidadInstitucional.trim() && perfil.departamentoUnidad.trim()) {
        resultado.unidadInstitucional = perfil.departamentoUnidad.trim();
        fuentesAplicadas.push('unidad institucional');
      }
    }

    if (cv) {
      if (!resultado.lineaInvestigacion.trim() && cv.lineasInvestigacion[0]?.linea?.trim()) {
        resultado.lineaInvestigacion = cv.lineasInvestigacion[0].linea.trim();
        fuentesAplicadas.push('linea desde curriculum');
      } else if (!resultado.lineaInvestigacion.trim() && cv.proyectos[0]?.nombreProyecto?.trim()) {
        resultado.lineaInvestigacion = cv.proyectos[0].nombreProyecto.trim();
        fuentesAplicadas.push('linea desde curriculum');
      }

      const cvAdjunto = cv.adjuntos.find((adjunto) => adjunto.tipo === 'cv_pdf');
      const yaTieneCv = resultado.documentosAdjuntos.some((documento) => documento.tipo === 'cv_postulante');
      if (cvAdjunto && !yaTieneCv) {
        const extension = cvAdjunto.nombreArchivo.split('.').pop()?.toLowerCase() ?? 'pdf';
        resultado.documentosAdjuntos = [
          ...resultado.documentosAdjuntos,
          {
            id: `cv-postulante-${cvAdjunto.nombreArchivo.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
            tipo: 'cv_postulante',
            nombreArchivo: cvAdjunto.nombreArchivo,
            extension,
            tamanoBytes: cvAdjunto.sizeKb * 1024,
            obligatorio: true,
            estado: 'cargado',
            fechaCarga: new Date().toISOString(),
            origen: 'curriculum',
          },
        ];
        fuentesAplicadas.push('CV reutilizable');
      }
    }

    resultado.instituciones = this.clonarInstituciones(resultado.instituciones, {
      institucionPatrocinante: resultado.institucionPatrocinante,
      unidadInstitucional: resultado.unidadInstitucional,
      departamentoFacultad: '',
    });

    this.mensajeAutocompletado = fuentesAplicadas.length
      ? `Se precargaron datos desde Mi perfil y Curriculum: ${[...new Set(fuentesAplicadas)].join(', ')}. Puedes editarlos si lo necesitas.`
      : '';

    return resultado;
  }

  private resolverDepartamentoFacultad(valorActual: string, perfil?: DatosPersonales): string {
    const valorNormalizado = valorActual.trim();
    if (valorNormalizado && valorNormalizado.toLowerCase() !== 'por definir') {
      return valorActual;
    }

    const desdePerfil = perfil?.departamentoUnidad?.trim() ?? '';
    if (desdePerfil) {
      if (!this.mensajeAutocompletado.includes('departamento / facultad')) {
        const base = this.mensajeAutocompletado.replace(/\.$/, '');
        this.mensajeAutocompletado = base
          ? `${base}, departamento / facultad. Puedes editarlos si lo necesitas.`
          : 'Se precargaron datos desde Mi perfil y Curriculum: departamento / facultad. Puedes editarlos si lo necesitas.';
      }
      return desdePerfil;
    }

    return valorActual;
  }

  private validarInformacionGeneral(): boolean {
    const obligatorios = [
      this.formulario.nombreInstitucion,
      this.formulario.nombreProgramaEstudios,
      this.formulario.regionProgramaEstudios,
      this.formulario.especialidadWos,
      this.formulario.subAreaOcde,
      this.formulario.fechaInicioEstudios,
      this.formulario.estadoProgramaEstudios,
    ];

    if (this.mostrarSeccionProyecto) {
      obligatorios.push(this.formulario.nombreProyecto, this.formulario.resumenProyecto, this.formulario.objetivoGeneral);
    }

    return obligatorios.every((valor) => String(valor ?? '').trim().length > 0);
  }

  private validarInstituciones(): boolean {
    return this.clonarInstituciones(this.formulario.instituciones, {
      institucionPatrocinante: this.formulario.institucionPatrocinante,
      unidadInstitucional: this.formulario.unidadInstitucional,
      departamentoFacultad: this.formulario.departamentoFacultad,
    }).some((institucion) =>
      [institucion.nombreInstitucion, institucion.unidadInstitucional, institucion.departamentoFacultad].every(
      (valor) => String(valor ?? '').trim().length > 0
      )
    );
  }

  private clonarInstituciones(
    instituciones: InstitucionFormulario[] | undefined,
    fallback?: {
      institucionPatrocinante: string;
      unidadInstitucional: string;
      departamentoFacultad: string;
    }
  ): InstitucionFormulario[] {
    const normalizadas = (instituciones ?? [])
      .map((institucion, index): InstitucionFormulario => ({
        nombreInstitucion: this.normalizarValorInstitucion(institucion.nombreInstitucion),
        unidadInstitucional: this.normalizarValorInstitucion(institucion.unidadInstitucional),
        departamentoFacultad: this.normalizarValorInstitucion(institucion.departamentoFacultad),
        rol: index === 0 ? 'principal' : institucion.rol === 'principal' ? 'asociada' : institucion.rol,
      }))
      .filter((institucion) =>
        [institucion.nombreInstitucion, institucion.unidadInstitucional, institucion.departamentoFacultad].every((valor) => valor.length > 0)
      );

    if (normalizadas.length > 0) return normalizadas;
    if (!fallback) return [];

    const principal = {
      nombreInstitucion: this.normalizarValorInstitucion(fallback.institucionPatrocinante),
      unidadInstitucional: this.normalizarValorInstitucion(fallback.unidadInstitucional),
      departamentoFacultad: this.normalizarValorInstitucion(fallback.departamentoFacultad),
      rol: 'principal' as const,
    };

    return [principal.nombreInstitucion, principal.unidadInstitucional, principal.departamentoFacultad].every((valor) => valor.length > 0)
      ? [principal]
      : [];
  }

  private clonarPresupuestoItems(items: PresupuestoItemFormulario[] | undefined): PresupuestoItemFormulario[] {
    return (items ?? []).map((item) => ({
      item: item.item,
      subitems: item.subitems.map((subitem) => ({
        nombre: subitem.nombre,
        montosPorAnio: (subitem.montosPorAnio ?? []).map((monto) => Number(monto) || 0),
      })),
    }));
  }

  private clonarPresupuestoPorInstitucion(items: PresupuestoInstitucionFormulario[] | undefined): PresupuestoInstitucionFormulario[] {
    return (items ?? []).map((presupuesto) => ({
      nombreInstitucion: this.normalizarValorInstitucion(presupuesto.nombreInstitucion),
      unidadInstitucional: this.normalizarValorInstitucion(presupuesto.unidadInstitucional),
      departamentoFacultad: this.normalizarValorInstitucion(presupuesto.departamentoFacultad),
      rolInstitucion: presupuesto.rolInstitucion,
      items: this.clonarPresupuestoItems(presupuesto.items),
    }));
  }

  private prepararPresupuestoPorInstitucion(): void {
    const instituciones = this.clonarInstituciones(this.formulario.instituciones, {
      institucionPatrocinante: this.formulario.institucionPatrocinante,
      unidadInstitucional: this.formulario.unidadInstitucional,
      departamentoFacultad: this.formulario.departamentoFacultad,
    });

    if (instituciones.length === 0) {
      this.formulario.presupuestoPorInstitucion = [];
      this.formulario.presupuestoItems = [];
      this.sincronizarTotalesPresupuesto();
      return;
    }

    const existentes = this.formulario.presupuestoPorInstitucion ?? [];
    const legacyItems = this.clonarPresupuestoItems(this.formulario.presupuestoItems);
    const hayTotalesPlanos = this.tieneTotalesPlanosPresupuesto();

    this.formulario.presupuestoPorInstitucion = instituciones.map((institucion, index) => {
      const existente = this.buscarPresupuestoExistente(institucion, existentes);
      const itemsExistentes = existente?.items?.length ? this.clonarPresupuestoItems(existente.items) : [];
      const itemsLegacy = index === 0 && legacyItems.length ? this.clonarPresupuestoItems(legacyItems) : [];
      const itemsBase = itemsExistentes.length
        ? itemsExistentes
        : itemsLegacy.length
          ? itemsLegacy
          : this.crearPresupuestoBase(index === 0 && hayTotalesPlanos);

      return {
        nombreInstitucion: institucion.nombreInstitucion,
        unidadInstitucional: institucion.unidadInstitucional,
        departamentoFacultad: institucion.departamentoFacultad,
        rolInstitucion: institucion.rol,
        items: this.normalizarPresupuestoItems(itemsBase),
      };
    });

    this.sincronizarTotalesPresupuesto();
  }

  private buscarPresupuestoExistente(
    institucion: InstitucionFormulario,
    existentes: PresupuestoInstitucionFormulario[]
  ): PresupuestoInstitucionFormulario | undefined {
    const clave = this.claveInstitucionPresupuesto(institucion);
    return (
      existentes.find((presupuesto) => this.clavePresupuestoInstitucion(presupuesto) === clave) ??
      existentes.find((presupuesto) => presupuesto.nombreInstitucion === institucion.nombreInstitucion)
    );
  }

  private crearPresupuestoBase(usarTotalesPlanos = false): PresupuestoItemFormulario[] {
    const monto = (valor: number): number => (usarTotalesPlanos ? Number(valor) || 0 : 0);

    return [
      {
        item: 'Gastos en Personal',
        subitems: [this.crearPresupuestoSubitem('Recursos humanos', monto(this.formulario.recursosHumanos))],
      },
      {
        item: 'Gastos de Operacion',
        subitems: [
          this.crearPresupuestoSubitem('Operacion y funcionamiento', monto(this.formulario.operacionFuncionamiento)),
          this.crearPresupuestoSubitem('Viajes y viaticos', monto(this.formulario.viajesViaticos)),
          this.crearPresupuestoSubitem('Publicaciones y difusion', monto(this.formulario.publicacionesDifusion)),
          this.crearPresupuestoSubitem('Servicios externos', monto(this.formulario.serviciosExternos)),
          this.crearPresupuestoSubitem('Software y licencias', monto(this.formulario.softwareLicencias)),
          this.crearPresupuestoSubitem('Capacitacion y formacion', monto(this.formulario.capacitacionFormacion)),
          this.crearPresupuestoSubitem('Materiales e insumos', monto(this.formulario.materialesInsumos)),
        ],
      },
      {
        item: 'Infraestructura y Mobiliario',
        subitems: [this.crearPresupuestoSubitem('Infraestructura', monto(this.formulario.infraestructura))],
      },
      {
        item: 'Equipamiento',
        subitems: [this.crearPresupuestoSubitem('Equipamiento', monto(this.formulario.equipamiento))],
      },
    ];
  }

  private crearPresupuestoSubitem(nombre: string, totalActual: number): PresupuestoSubitemFormulario {
    return {
      nombre,
      montosPorAnio: this.aniosPresupuesto().map((_, index) => (index === 0 ? Number(totalActual) || 0 : 0)),
    };
  }

  private normalizarPresupuestoItems(items: PresupuestoItemFormulario[]): PresupuestoItemFormulario[] {
    return items.map((item) => ({
      ...item,
      subitems: item.subitems.map((subitem) => ({
        ...subitem,
        montosPorAnio: this.montosPresupuestoPersistentes(subitem),
      })),
    }));
  }

  private montosPresupuestoPersistentes(subitem: PresupuestoSubitemFormulario): number[] {
    const montosActuales = subitem.montosPorAnio ?? [];
    const cantidad = Math.max(this.aniosPresupuesto().length, montosActuales.length);
    return Array.from({ length: cantidad }, (_, index) => Number(montosActuales[index]) || 0);
  }

  private sincronizarTotalesPresupuesto(): void {
    const todosLosItems = (this.formulario.presupuestoPorInstitucion ?? []).flatMap((presupuesto) => presupuesto.items);
    const totalPorNombre = (nombre: string): number =>
      todosLosItems
        .flatMap((item) => item.subitems)
        .filter((subitem) => subitem.nombre === nombre)
        .reduce((total, subitem) => total + this.totalPresupuestoSubitem(subitem), 0);

    this.formulario.presupuestoItems = this.clonarPresupuestoItems(this.formulario.presupuestoPorInstitucion[0]?.items ?? []);
    this.formulario.recursosHumanos = totalPorNombre('Recursos humanos');
    this.formulario.equipamiento = totalPorNombre('Equipamiento');
    this.formulario.infraestructura = totalPorNombre('Infraestructura');
    this.formulario.operacionFuncionamiento = totalPorNombre('Operacion y funcionamiento');
    this.formulario.viajesViaticos = totalPorNombre('Viajes y viaticos');
    this.formulario.publicacionesDifusion = totalPorNombre('Publicaciones y difusion');
    this.formulario.serviciosExternos = totalPorNombre('Servicios externos');
    this.formulario.softwareLicencias = totalPorNombre('Software y licencias');
    this.formulario.capacitacionFormacion = totalPorNombre('Capacitacion y formacion');
    this.formulario.materialesInsumos = totalPorNombre('Materiales e insumos');
    this.formulario.totalSolicitado = todosLosItems.reduce((total, item) => {
      return total + item.subitems.reduce((subtotal, subitem) => subtotal + this.totalPresupuestoSubitem(subitem), 0);
    }, 0);
  }

  private totalPresupuestoSubitem(subitem: PresupuestoSubitemFormulario): number {
    return this.aniosPresupuesto().reduce((total, _, index) => total + (Number(subitem.montosPorAnio?.[index]) || 0), 0);
  }

  private tieneTotalesPlanosPresupuesto(): boolean {
    return [
      this.formulario.recursosHumanos,
      this.formulario.equipamiento,
      this.formulario.infraestructura,
      this.formulario.operacionFuncionamiento,
      this.formulario.viajesViaticos,
      this.formulario.publicacionesDifusion,
      this.formulario.serviciosExternos,
      this.formulario.softwareLicencias,
      this.formulario.capacitacionFormacion,
      this.formulario.materialesInsumos,
    ].some((valor) => Number(valor) > 0);
  }

  private aniosPresupuesto(): number[] {
    const cantidadAnios = Number(this.formulario.duracionPresupuestoAnios) || 3;
    return Array.from({ length: Math.max(1, Math.trunc(cantidadAnios)) }, (_, index) => index + 1);
  }

  private claveInstitucionPresupuesto(institucion: InstitucionFormulario): string {
    return [institucion.nombreInstitucion, institucion.unidadInstitucional, institucion.departamentoFacultad]
      .map((valor) => String(valor ?? '').trim().toLowerCase())
      .join('|');
  }

  private clavePresupuestoInstitucion(presupuesto: PresupuestoInstitucionFormulario): string {
    return [presupuesto.nombreInstitucion, presupuesto.unidadInstitucional, presupuesto.departamentoFacultad]
      .map((valor) => String(valor ?? '').trim().toLowerCase())
      .join('|');
  }

  private resolverDuracionPresupuestoAnios(
    formularioData: Pick<FormularioPostulacionData, 'duracionPresupuestoAnios' | 'presupuestoItems' | 'presupuestoPorInstitucion'>
  ): number {
    const duracionBackend = Number(formularioData.duracionPresupuestoAnios);
    if (Number.isFinite(duracionBackend) && duracionBackend > 0) return Math.trunc(duracionBackend);

    const items = [
      ...(formularioData.presupuestoItems ?? []),
      ...(formularioData.presupuestoPorInstitucion ?? []).flatMap((presupuesto) => presupuesto.items),
    ];

    const duracionDesdeItems = items.reduce((maximo, item) => {
      const maximoSubitems = item.subitems.reduce((maximoSubitem, subitem) => {
        return Math.max(maximoSubitem, subitem.montosPorAnio?.length ?? 0);
      }, 0);

      return Math.max(maximo, maximoSubitems);
    }, 0);

    return duracionDesdeItems > 0 ? duracionDesdeItems : 3;
  }

  private normalizarValorInstitucion(valor: string | undefined): string {
    const limpio = String(valor ?? '').trim();
    return this.esValorInstitucionReal(limpio) ? limpio : '';
  }

  private esValorInstitucionReal(valor: string | undefined): boolean {
    const normalizado = String(valor ?? '').trim().toLowerCase();
    return normalizado.length > 0 && normalizado !== 'por definir';
  }

  private validarEquipoTrabajo(): boolean {
    return [this.formulario.recomendadorAsignado, this.formulario.colaboradorAsignado].every(
      (valor) => String(valor ?? '').trim().length > 0
    );
  }

  private validarPresupuesto(): boolean {
    return this.formulario.totalSolicitado > 0;
  }

  private validarDocumentos(): boolean {
    const tiposObligatorios = ['carta_compromiso', 'cv_postulante', 'presupuesto_financiero'] as const;
    return tiposObligatorios.every((tipo) =>
      this.formulario.documentosAdjuntos.some((documento) => documento.tipo === tipo && documento.estado === 'cargado')
    );
  }

  private construirPostulacionActual(): Postulacion {
    return {
      ...this.postulacion!,
      nombreProyecto: this.formulario.nombreProyecto.trim() || this.postulacion!.nombreProyecto,
      departamentoFacultad: this.formulario.departamentoFacultad.trim() || this.postulacion!.departamentoFacultad,
      formularioData: this.extraerFormularioData(),
    };
  }

  private crearAvanceSeccion(
    key: PasoFormulario,
    label: string,
    detalle: string,
    campos: Array<string | number | boolean>
  ): AvanceSeccionFormulario {
    const total = campos.length || 1;
    const completados = campos.filter((campo) => this.estaCompleto(campo)).length;

    return {
      key,
      label,
      detalle,
      completados,
      total,
      porcentaje: Math.round((completados / total) * 100),
      completa: completados === total,
    };
  }

  private estaCompleto(valor: string | number | boolean): boolean {
    if (typeof valor === 'number') {
      return valor > 0;
    }

    if (typeof valor === 'boolean') {
      return valor;
    }

    return String(valor ?? '').trim().length > 0;
  }

  private tieneDocumentoCargado(tipo: FormularioPostulacionData['documentosAdjuntos'][number]['tipo']): boolean {
    return this.formulario.documentosAdjuntos.some((documento) => documento.tipo === tipo && documento.estado === 'cargado');
  }

  private resolverPermisos(item: Postulacion | null): PermisosFormulario {
    if (!item) {
      return {
        visible: false,
        modo: 'solo_lectura',
        puedeGuardar: false,
        puedeEnviar: false,
        motivo: 'No fue posible encontrar la postulacion solicitada.',
      };
    }

    const puedeObservar = this.rol === 'analista' || this.rol === 'urc';

    if (!item.rolesPermitidos.includes(this.rol) && !puedeObservar) {
      return {
        visible: false,
        modo: 'solo_lectura',
        puedeGuardar: false,
        puedeEnviar: false,
        motivo: 'La postulacion ya no esta disponible para este rol.',
      };
    }

    const enviada = item.enviada ?? item.enviadaPorPostulante;
    const estadoEditable = item.estadoPostulacion === 'en_preparacion' || item.estadoPostulacion === 'observada';

    if (this.rol === 'ayudante_postulante') {
      if (item.ayudanteActivo === false) {
        return {
          visible: false,
          modo: 'solo_lectura',
          puedeGuardar: false,
          puedeEnviar: false,
          motivo: 'El postulante ya no mantiene permisos activos para este ayudante.',
        };
      }

      const puedeEditar =
        item.puedeEditar ??
        (item.editablePorAyudante && estadoEditable && !item.bloqueadaPorRechazoPatrocinio);

      return {
        visible: true,
        modo: puedeEditar ? 'edicion' : 'solo_lectura',
        puedeGuardar: puedeEditar,
        puedeEnviar: false,
        motivo: puedeEditar ? undefined : 'La postulacion ya fue enviada o el ayudante no mantiene permisos de edicion.',
      };
    }

    if (this.rol === 'postulante') {
      const puedeEditar = item.puedeEditar ?? (estadoEditable && !item.bloqueadaPorRechazoPatrocinio);
      return {
        visible: true,
        modo: puedeEditar ? 'edicion' : 'solo_lectura',
        puedeGuardar: puedeEditar,
        puedeEnviar: item.puedeEnviar ?? estadoEditable,
        motivo: puedeEditar ? undefined : 'La postulacion se encuentra disponible solo para consulta en esta etapa.',
      };
    }

    if (this.rol === 'analista' || this.rol === 'urc') {
      return {
        visible: true,
        modo: 'solo_lectura',
        puedeGuardar: false,
        puedeEnviar: false,
        motivo: 'Visualizacion en modo observador desde el modulo de monitoreo.',
      };
    }

    return {
      visible: true,
      modo: 'solo_lectura',
      puedeGuardar: false,
      puedeEnviar: false,
      motivo: 'Este rol solo puede visualizar el formulario de la postulacion.',
    };
  }

  private mapearTextosFormulario(formulario: FormularioConcursoAdmin | null): TextosFormularioConcurso | null {
    if (!formulario) return null;

    const nombreFormulario = formulario.nombreFormulario.trim();
    const descripcion = formulario.descripcion.trim();
    const instrucciones = formulario.instruccionesPostulantes.trim();
    const mensajeSistema = formulario.mensajeInformativoSistema.trim();

    if (!nombreFormulario && !descripcion && !instrucciones && !mensajeSistema) {
      return null;
    }

    return {
      nombreFormulario,
      descripcion,
      instrucciones,
      mensajeSistema,
    };
  }
}

